/**
 * Utility functions for formatting data
 */

/**
 * Formats a number as currency
 * @param {number} value - The value to format
 * @param {string} currency - Currency code (default: USD)
 * @returns {string} Formatted currency string
 */
export function formatCurrency(value, currency = 'USD') {
  if (value == null) return '-';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency
  }).format(value);
}

/**
 * Formats a date string for display
 * @param {string|Date} date - The date to format
 * @returns {string} Formatted date string
 */
export function formatDate(date) {
  if (!date) return '-';
  return new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC'
  }).format(new Date(date));
}

/**
 * Formats a date for input fields (YYYY-MM-DD)
 * @param {string|Date} date - The date to format
 * @returns {string} ISO date string
 */
export function formatDateForInput(date) {
  if (!date) return '';
  const d = new Date(date);
  return d.toISOString().split('T')[0];
}

/**
 * Formats age in months to years and months
 * @param {number} months - Age in months
 * @returns {string} Formatted age string
 */
export function formatAge(months) {
  if (months == null) return '-';
  const years = Math.floor(months / 12);
  const remainingMonths = months % 12;

  if (years === 0) {
    return `${remainingMonths}mo`;
  }
  if (remainingMonths === 0) {
    return `${years}yr`;
  }
  return `${years}yr ${remainingMonths}mo`;
}

/**
 * Formats proof value
 * @param {number} proof - Proof value
 * @returns {string} Formatted proof string
 */
export function formatProof(proof) {
  if (proof == null) return '-';
  return `${proof}°`;
}

/**
 * Truncates text to specified length
 * @param {string} text - Text to truncate
 * @param {number} maxLength - Maximum length
 * @returns {string} Truncated text with ellipsis if needed
 */
export function truncateText(text, maxLength = 50) {
  if (!text) return '';
  if (text.length <= maxLength) return text;
  return `${text.substring(0, maxLength)}...`;
}
